// The ONE product-card style panel, shared by the Home and Search page editors.
//
// Both editors used to split the same four dictionaries across two sibling buttons and two panels —
// a "Card Style…" panel with Card / Name / Price Pivot tabs, plus a separate "Image…" panel — even
// though the four dicts are parts of a single visual object (and are exactly the parts a card-type
// Storefront Style carries: card / title / price / image / hover). Tabs also hid three quarters of
// the card behind a click and made the $styleRef pickers feel unrelated.
//
// So: one button, one panel, a FLAT surface built the same way as the section editor's Appearance
// tab (StyleGroupHeader + StyleOverridesGroup + flat rows, no Pivot), with a live fake-product
// example pinned at the top so every edit is visible without hunting for a card in the preview.
// The example takes the shape the page actually renders (card, or row for the Search page's Rows
// view), because the same four dicts land very differently on the two layouts.
//
// The panel is deliberately dumb: it owns no state and parses no config. Each editor passes the
// four (or five) dicts out of its own config blob plus the matching updaters, and `configField`
// says which blob they came from so the style pickers resolve against the right allow-list
// (styleTargetForConfigKey — `title` means "card name" here but "page heading" on the cart page).

import React from 'react';
import { Panel, PanelType } from '@fluentui/react';
import { ISearchCardStyle, ISearchElementStyle, ISearchPriceStyle, ISearchTextStyle } from './searchPageConfig';
import {
    cardStyleFrom,
    elementStyleFrom,
    ElementPropertyRows,
    IProductCardStyles,
    priceStyleFrom,
    StyleGroupHeader,
    StyleOverridesGroup,
    textStyleFrom,
} from './styleControls';
import { stylePartForConfigKey, styleTargetForConfigKey } from './styleBulkApply';
import { resolveHoverPart, STYLE_PART_ICONS } from './stylesConfig';
import ProductCardExample, { ProductRowExample } from './ProductCardExample';

export interface IProductCardStylePanelProps {
    isOpen: boolean;
    onDismiss: () => void;
    /** 'Card' | 'Row' — the Search page's rows mode labels everything "Row"; Home is always 'Card'. */
    modeNoun: 'Card' | 'Row';
    /** Which config blob these dicts live in — drives styleTargetForConfigKey / stylePartForConfigKey. */
    configField: 'homePageConfig' | 'searchPageConfig';
    card?: ISearchCardStyle;
    onCardChange: (patch: Partial<ISearchCardStyle>) => void;
    title?: ISearchTextStyle;
    onTitleChange: (patch: Partial<ISearchTextStyle>) => void;
    price?: ISearchPriceStyle;
    onPriceChange: (patch: Partial<ISearchPriceStyle>) => void;
    image?: ISearchElementStyle;
    onImageChange: (patch: Partial<ISearchElementStyle>) => void;
    /**
     * The presence of the handler is what renders the Hover group — only offer it where the host
     * page's renderer actually emits a `cardHover` :hover rule (Home and Search both do).
     */
    cardHover?: ISearchElementStyle;
    onCardHoverChange?: (patch: Partial<ISearchElementStyle>) => void;
    isDark?: boolean;
}

// ISearchCardStyle / ISearchTextStyle / ISearchPriceStyle are narrower than the ISearchElementStyle
// the generic rows speak, and they disagree on a couple of named props (price's `padding` is a CSS
// string, the element rows' is a px number), so the dicts don't structurally interchange in EITHER
// direction — even though at RUNTIME they're all open camelCase CSS bags with an index signature,
// and a row writing e.g. `padding: 8` into the price dict is perfectly valid there.
// So one loose cast at the boundary, in both directions (value in, patch out) — exactly like
// `asStyle` in StorefrontStylesPanel and the deliberately-loose dicts StyleRefPicker takes.
const loose = (dict?: { [prop: string]: any }): any => dict;

const ProductCardStylePanel: React.FC<IProductCardStylePanelProps> = (props) => {
    const { configField, modeNoun } = props;

    // Same props either way (IProductCardExampleProps), so the mode picks the component and
    // nothing below has to care which shape is on screen.
    const Example = modeNoun === 'Row' ? ProductRowExample : ProductCardExample;

    // The example repaints from the same builders the page previews use, so a $styleRef picked in
    // this panel shows up here immediately — the builders resolve the ref against the active styles
    // registry, and the second argument is the part implied by the key the dict lives on.
    const cardStyles: IProductCardStyles = {
        card: cardStyleFrom(props.card, stylePartForConfigKey(configField, 'card')),
        title: textStyleFrom(props.title, stylePartForConfigKey(configField, 'title')),
        price: priceStyleFrom(props.price, stylePartForConfigKey(configField, 'price')),
        image: elementStyleFrom(props.image, stylePartForConfigKey(configField, 'image')),
    };

    // Hover: exactly the renderer's pipeline — resolveHoverPart picks ONE ref (the hover dict's
    // own, else the resting card's) and puts that preset's `hover` part UNDER the local cardHover
    // dict. Computed even when the Hover group isn't wired: the renderer emits a referenced style's
    // hover part regardless, so the example must too.
    const hoverStyle: React.CSSProperties =
        elementStyleFrom(loose(resolveHoverPart(props.cardHover, props.card)), 'hover');

    return (
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            headerText={`Product ${modeNoun}s`}
            type={PanelType.smallFixedFar}
            isBlocking={false}
            closeButtonAriaLabel='Close'
        >
            {/* Pinned example: a fake product drawn from the live dicts. Sits above the groups so
                the effect of every row below is on screen without scrolling back to the preview —
                and stays pinned while the groups scroll, on viewports tall enough to afford it
                (sf-card-example-sticky, StorefrontEditor.css).
                The example follows modeNoun: the four dicts style a completely different shape in
                the Search page's Rows view, so a panel titled "Product Rows" shows a row. Both
                flavors take the same props and share the frame + caption. */}
            <div className='sf-card-example-sticky' style={{ marginTop: 8 }}>
                <Example
                    cardStyles={cardStyles}
                    hoverStyle={hoverStyle}
                    isDark={props.isDark}
                    caption={`Example ${modeNoun.toLowerCase()} — updates as you edit`}
                />
            </div>

            {/* A FLAT surface, mirroring the section editor's Appearance tab: one lightweight header
                row per part, its named-style picker always visible underneath, and only the raw CSS
                rows folded away behind StyleOverridesGroup's "Advanced overrides (N)" chevron. No
                Pivot — the four parts are one object, and tabs made three of them invisible.
                No StyleRowsWidthProvider: this is a 340px smallFixedFar panel, the same width the
                Styles panel's rows were tuned for, so the default 180px control column fits. */}
            <StyleGroupHeader
                label={modeNoun}
                iconName={STYLE_PART_ICONS.card}
                tooltip={`The ${modeNoun.toLowerCase()} tile itself: background, border, corner radius and shadow.`}
            />
            <StyleOverridesGroup
                pickerLabel='Style'
                rowLayout
                target={styleTargetForConfigKey(configField, 'card')}
                value={props.card}
                onChange={props.onCardChange}
            >
                <ElementPropertyRows value={loose(props.card)} settingKeyPrefix='storefront.productCard.card' onChange={(patch) => props.onCardChange(loose(patch))} />
            </StyleOverridesGroup>

            <StyleGroupHeader
                label='Name'
                iconName={STYLE_PART_ICONS.title}
                tooltip='The product name. Font settings here style that line only.'
            />
            <StyleOverridesGroup
                pickerLabel='Style'
                rowLayout
                target={styleTargetForConfigKey(configField, 'title')}
                value={props.title}
                onChange={props.onTitleChange}
            >
                <ElementPropertyRows value={loose(props.title)} settingKeyPrefix='storefront.productCard.title' onChange={(patch) => props.onTitleChange(loose(patch))} />
            </StyleOverridesGroup>

            <StyleGroupHeader
                label='Price'
                iconName={STYLE_PART_ICONS.price}
                tooltip='The price line. Setting a background, border or padding wraps it in its own pill.'
            />
            <StyleOverridesGroup
                pickerLabel='Style'
                rowLayout
                target={styleTargetForConfigKey(configField, 'price')}
                value={props.price}
                onChange={props.onPriceChange}
            >
                <ElementPropertyRows value={loose(props.price)} settingKeyPrefix='storefront.productCard.price' onChange={(patch) => props.onPriceChange(loose(patch))} />
            </StyleOverridesGroup>

            <StyleGroupHeader
                label='Image'
                iconName={STYLE_PART_ICONS.image}
                tooltip='The product image area — its backdrop, border and padding, not the picture itself.'
            />
            <StyleOverridesGroup
                pickerLabel='Style'
                rowLayout
                target={styleTargetForConfigKey(configField, 'image')}
                value={props.image}
                onChange={props.onImageChange}
            >
                <ElementPropertyRows value={props.image} settingKeyPrefix='storefront.productCard.image' onChange={props.onImageChange} />
            </StyleOverridesGroup>

            {/* Hover: only where the renderer actually emits the rule (Search). Until now `cardHover`
                was reachable only by hand-editing the raw JSON. */}
            {props.onCardHoverChange && <>
                <StyleGroupHeader
                    label='Hover'
                    iconName={STYLE_PART_ICONS.hover}
                    tooltip={`Applied while the shopper's pointer is over the ${modeNoun.toLowerCase()} — the storefront emits it as a scoped :hover rule with a short transition.`}
                />
                <StyleOverridesGroup
                    pickerLabel='Style'
                    rowLayout
                    target={styleTargetForConfigKey(configField, 'cardHover')}
                    value={props.cardHover}
                    onChange={props.onCardHoverChange}
                >
                    <ElementPropertyRows value={props.cardHover} settingKeyPrefix='storefront.productCard.hover' onChange={props.onCardHoverChange} />
                </StyleOverridesGroup>
            </>}
        </Panel>
    );
};

export default ProductCardStylePanel;
